import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import NewSessionDialog from '../components/NewSessionDialog';
import EditSessionDialog from '../components/EditSessionDialog';
import TrainingNotificationDialog from '../components/TrainingNotificationDialog';
import AttendanceConfirmationDialog from '../components/AttendanceConfirmationDialog';
import SessionList from '../components/schedule/SessionList';
import ScheduleManagement from '../components/schedule/ScheduleManagement';
import RecurringScheduleDialog from '../components/schedule/RecurringScheduleDialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from '../contexts/AuthContext';
import { Calendar, TrendingUp, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTrainingNotifications } from '../hooks/useTrainingNotifications';
import { useSessionManagement } from '../hooks/useSessionManagement';

interface TrainingSession {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
  max_participants: number | null;
  instructor_id: string | null;
  created_at: string;
  attendance_count?: number;
  age_group?: 'all' | 'kids' | 'teens' | 'adults';
  status?: 'scheduled' | 'completed' | 'cancelled';
}

const TrainingSchedule = () => {
  useUiLanguage();
  const { t } = useTranslation();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [newSessionDialogOpen, setNewSessionDialogOpen] = useState(false);
  const [editSessionDialogOpen, setEditSessionDialogOpen] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<TrainingSession | null>(null);
  const [isClosingSessions, setIsClosingSessions] = useState(false);

  const {
    sessions,
    promotionSessions,
    isLoading,
    fetchTrainingSessions,
    confirmAttendance,
    confirmPromotionAttendance,
    deleteSession,
    closePastSessions
  } = useSessionManagement();

  const { pendingSession, dialogOpen, closePendingNotification } = useTrainingNotifications();

  const handleEditSession = (session: TrainingSession) => {
    setSelectedSession(session);
    setEditSessionDialogOpen(true);
  };

  const handleSendNotification = (session: TrainingSession) => {
    setSelectedSession(session);
    setNotificationDialogOpen(true);
  };

  const handleConfirmAttendance = (session: TrainingSession) => {
    setSelectedSession(session);
    setAttendanceDialogOpen(true);
  };

  const handleManualClosePastSessions = async () => {
    setIsClosingSessions(true);
    try {
      await closePastSessions();
    } catch (error) {
      console.error('Failed to close past sessions:', error);
    } finally {
      setIsClosingSessions(false);
    }
  };

  if (isLoading) {
    return (
      <Layout title={t("common:navigation.trainingSchedule")}>
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-900 mx-auto mb-4"></div>
              <p>{t("common:messages.loading")}</p>
            </div>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout title={t("common:navigation.trainingSchedule")}>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t("common:navigation.trainingSchedule")}
              </div>
              <Button
                onClick={() => navigate((profile?.role === 'admin' || profile?.role === 'super_user') ? '/admin' : '/dashboard')}
                variant="outline"
                size="sm"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {uiText("Back to Dashboard")}</Button>
            </div>
            <CardDescription>
              {uiText("All training sessions at our dojo. Times are displayed in your local timezone.")}</CardDescription>
          </CardHeader>
          {(profile?.role === 'admin' || profile?.role === 'super_user') && (
            <CardContent>
              <Button
                onClick={handleManualClosePastSessions}
                disabled={isClosingSessions}
                variant="outline"
                className="border-red-200 text-red-700 hover:bg-red-50"
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                {isClosingSessions ? uiText("Closing Sessions...") : uiText("Close Past Sessions & Update Stats")}
              </Button>
            </CardContent>
          )}
        </Card>

        {(profile?.role === 'admin' || profile?.role === 'super_user') && (
          <Card>
            <CardHeader>
              <CardTitle>{uiText("Quick Actions")}</CardTitle>
              <CardDescription>{uiText("Create recurring schedules and manage training sessions")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <RecurringScheduleDialog onScheduleCreated={fetchTrainingSessions} />
              </div>
            </CardContent>
          </Card>
        )}

        {(profile?.role === 'admin' || profile?.role === 'super_user') && (
          <ScheduleManagement
            sessions={sessions}
            onNewSession={() => setNewSessionDialogOpen(true)}
          />
        )}

        <SessionList
          sessions={sessions}
          promotionSessions={promotionSessions}
          userRole={profile?.role}
          onEdit={handleEditSession}
          onDelete={deleteSession}
          onNotify={handleSendNotification}
          onConfirmAttendance={handleConfirmAttendance}
          onConfirmPromotionAttendance={confirmPromotionAttendance}
        />
      </div>

      <NewSessionDialog
        open={newSessionDialogOpen}
        onOpenChange={setNewSessionDialogOpen}
        onSessionCreated={fetchTrainingSessions}
      />

      <EditSessionDialog
        open={editSessionDialogOpen}
        onOpenChange={setEditSessionDialogOpen}
        session={selectedSession}
        onSessionUpdated={fetchTrainingSessions}
      />

      <TrainingNotificationDialog
        open={notificationDialogOpen}
        onOpenChange={setNotificationDialogOpen}
        session={selectedSession}
      />

      <AttendanceConfirmationDialog
        open={dialogOpen}
        onOpenChange={closePendingNotification}
        session={pendingSession}
      />

      <AttendanceConfirmationDialog
        open={attendanceDialogOpen}
        onOpenChange={setAttendanceDialogOpen}
        session={selectedSession}
      />
    </Layout>
  );
};

export default TrainingSchedule;